import { resolveId } from '@medplum/core'
import type { Patient, Reference, Resource } from '@medplum/fhirtypes'
import * as appointments from '@seen/core/models/appointments/index'
import { Assessment } from '@seen/core/models/assessments/assessment'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { useRead } from '#src/hooks/useRead.ts'
import { useResource } from '#src/hooks/useResource.ts'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'
import { buildAppointmentRelativeUrl } from '#src/stores/calendar.ts'

/*
 * Builds a deep-link URL to a comment on its parent resource page.
 * The hash fragment is always emitted as `#item-<id>` going forward; the
 * `#comment-<id>` legacy format is still parsed by `getTimelineItemIdFromUrl`
 * for backwards compatibility with existing notifications and pasted links.
 */
// TODO: Combine with useCommentRelatedParticipant
export function useTimelineItemUrl(
  comment: CommentCommunication | undefined
): string {
  useWarningReporter(
    !!comment?.partOf &&
      comment.partOf.length !== 1 &&
      'Expected exactly one partOf reference'
  )
  const { data: parentCommentOrResource } = useResource(comment?.partOf?.[0])
  useWarningReporter(
    CommentCommunication.isInstance(parentCommentOrResource) &&
      !!parentCommentOrResource?.partOf &&
      parentCommentOrResource.partOf.length !== 1 &&
      'Expected exactly one partOf reference for parent comment'
  )
  const { data: maybeResource } = useResource(
    CommentCommunication.isInstance(parentCommentOrResource)
      ? parentCommentOrResource.partOf?.[0]
      : undefined
  )
  const resource = maybeResource ?? parentCommentOrResource

  const participantReference = getParticipantReference(resource)
  const { data: participant } = useRead(Participant, participantReference)

  if (Assessment.isInstance(resource)) {
    const assessment = Assessment.fromFhirResource(resource)
    return `${participant?.relativeUrl}/assessments/${assessment.id}#item-${comment?.id}`
  }

  if (StandardTask.isInstance(resource)) {
    const task = StandardTask.fromFhirResource(resource)
    if (participant) {
      return `${participant.relativeUrl}/tasks/${task.id}#item-${comment?.id}`
    }
    return `/tasks/${task.id}#item-${comment?.id}`
  }

  if (Order.isInstance(resource)) {
    const order = Order.fromFhirResource(resource)
    return `/Patient/${resolveId(order.participant)}/orders/${order.id}#item-${comment?.id}`
  }

  if (isOfType(resource, 'Patient')) {
    const participant = Participant.fromFhirResource(resource)
    return `${participant.relativeUrl}#item-${comment?.id}`
  }

  if (isOfType(resource, 'Appointment')) {
    const appointment =
      appointments.ExtendedAppointment.fromFhirResource(resource)
    return buildAppointmentRelativeUrl(appointment)
  }

  if (isOfType(resource, 'MedicationRequest')) {
    const medicationRequest =
      ExtendedMedicationRequest.fromFhirResource(resource)
    return `/Patient/${resolveId(medicationRequest.subject)}/medications/${medicationRequest.id}#item-${comment?.id}`
  }

  if (resource && SDR.isInstance(resource)) {
    const sdr = SDR.fromFhirResource(resource)
    return `/Patient/${resolveId(sdr.participant)}/sdr/${sdr.id}#item-${comment?.id}`
  }

  if (isOfType(resource, 'DocumentReference')) {
    const documentReference =
      ExtendedDocumentReference.fromFhirResource(resource)
    return `/Patient/${resolveId(documentReference.subject)}/documents?documentReferenceId=${documentReference.id}#item-${comment?.id}`
  }

  return '/'
}

/*
 * Parses the timeline-item id out of a URL hash. Accepts both the canonical
 * `#item-<id>` format and the legacy `#comment-<id>` format. Anything after a
 * subsequent `#` in the fragment is dropped so the returned value is always a
 * single ID safe to use as a Communication ID.
 */
export function getTimelineItemIdFromUrl(url: string): string | undefined {
  const id = extractAfter(url, '#item-') ?? extractAfter(url, '#comment-')
  return id?.split('#')[0]
}

function extractAfter(url: string, marker: string): string | undefined {
  const idx = url.indexOf(marker)
  if (idx === -1) {
    return undefined
  }
  return url.slice(idx + marker.length)
}

function getParticipantReference(
  resource: Resource | undefined
): Reference<Patient> | undefined {
  if (Assessment.isInstance(resource)) {
    const assessment = Assessment.fromFhirResource(resource)
    return assessment.subject
  }

  if (StandardTask.isInstance(resource)) {
    const task = StandardTask.fromFhirResource(resource)
    return task.participant
  }

  return undefined
}
